import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, BookOpen, MessageSquare, Wallet, Megaphone, CheckCheck, Loader2 } from "lucide-react";

const KIND_ICONS = {
  homework_new: BookOpen,
  homework_comment: MessageSquare,
  payment_new: Wallet,
  payment_due_soon: Wallet,
  admin_broadcast: Megaphone,
  system: Bell,
};

const KIND_COLORS = {
  homework_new: "text-emerald-600 bg-emerald-50",
  homework_comment: "text-blue-600 bg-blue-50",
  payment_new: "text-amber-600 bg-amber-50",
  payment_due_soon: "text-amber-600 bg-amber-50",
  admin_broadcast: "text-purple-600 bg-purple-50",
  system: "text-slate-600 bg-slate-50",
};

function relativeTime(iso, t) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return t("just_now");
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const timerRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data?.count || 0);
    } catch { /* ignore */ }
  }, [user]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 15 } });
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  // Poll unread count every 60s
  useEffect(() => {
    if (!user) return;
    fetchCount();
    timerRef.current = setInterval(fetchCount, 60_000);
    return () => clearInterval(timerRef.current);
  }, [user, fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  const onClick = async (n) => {
    try {
      if (!n.is_read) {
        await api.post(`/notifications/${n.id}/read`);
      }
    } catch { /* ignore */ }
    setOpen(false);
    fetchCount();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    setMarking(true);
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setCount(0);
    } finally {
      setMarking(false);
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notifications-bell"
          className="relative h-9 w-9"
          aria-label={t("notifications")}
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notifications-popover">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">{t("notifications")}</h3>
          {items.some((x) => !x.is_read) && (
            <button
              type="button"
              data-testid="mark-all-read-btn"
              onClick={markAll}
              disabled={marking}
              className="text-[11px] text-blue-700 hover:underline flex items-center gap-1"
            >
              <CheckCheck className="h-3 w-3" />
              {t("mark_all_read")}
            </button>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 px-6 text-center text-xs text-slate-500" data-testid="notifications-empty">
              {t("no_notifications")}
            </div>
          ) : (
            <ul>
              {items.map((n) => {
                const Icon = KIND_ICONS[n.kind] || Bell;
                const colors = KIND_COLORS[n.kind] || KIND_COLORS.system;
                return (
                  <li
                    key={n.id}
                    data-testid={`notification-${n.id}`}
                    onClick={() => onClick(n)}
                    className={`px-4 py-3 cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.is_read ? "bg-blue-50/40" : ""}`}
                  >
                    <div className={`h-8 w-8 rounded-full ${colors} flex items-center justify-center shrink-0`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-sm leading-snug truncate ${!n.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                          {n.title}
                        </p>
                        {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />}
                      </div>
                      {n.body && <p className="text-xs text-slate-600 leading-snug mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">{relativeTime(n.created_at, t)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 text-center">
          <button
            type="button"
            data-testid="view-all-notifications-btn"
            onClick={() => { setOpen(false); navigate("/dashboard/notifications"); }}
            className="text-xs text-blue-700 hover:underline"
          >
            {t("view_all_notifications")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
